import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Input, Typography } from "antd";
import { PlayCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

const Reproductor = ({ audio, transcript, start }) => {

  const audioRef = useRef(null);
  const activeRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [segments, setSegments] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [search, setSearch] = useState("");

  //////////////////////////////////////
  // convertir tiempo "0.300s"
  //////////////////////////////////////

  const parseTime = (t) => {
    if (!t) return 0;
    if (typeof t === "number") return t;
    return parseFloat(t.replace("s", ""));
  };

  //////////////////////////////////////
  // formato mm:ss
  //////////////////////////////////////

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2,"0")}`;
  };

  //////////////////////////////////////
  // cargar transcript
  //////////////////////////////////////
  const loadingRef = useRef(false);

  const loadTranscript = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setSegments([]);

    const res = await fetch(transcript);
    const json = await res.json();
    loadingRef.current = false;

    let segs = [];

    json.results?.forEach(r => {
      const alt = r.alternatives?.[0];
      if (!alt) return;
      const start = alt.words?.length > 0 ? parseTime(alt.words[0].startOffset) : 0;
      segs.push({ start, text: alt.transcript });
    });

    // Ordenamos por start
    segs.sort((a,b) => a.start - b.start);

    const cleaned = [];
    segs.forEach((s, i) => {
      if (i === 0) {
        cleaned.push(s);
        return;
      }

      const prev = cleaned[cleaned.length - 1];

      // Si el bloque comienza con texto repetido, lo recortamos
      if (s.text.includes(prev.text)) {
        const newText = s.text.slice(prev.text.length).trim();
        if (newText) cleaned.push({ start: s.start, text: newText });
        return;
      }

      // Si es texto totalmente igual, lo ignoramos
      if (s.text === prev.text) return;

      // Si el start es igual pero el texto es distinto, lo agregamos (para offsets de búsqueda)
      cleaned.push(s);
    });

    setSegments(cleaned);
  };

  //////////////////////////////////////
  // cargar transcript al abrir
  //////////////////////////////////////

  useEffect(() => {
    if (open) loadTranscript();
  }, [open]);

  //////////////////////////////////////
  // iniciar audio en startOffset
  //////////////////////////////////////

  useEffect(() => {

    if (!open) return;

    const audioEl = audioRef.current;
    if (!audioEl) return;

    const handleLoaded = () => {

      const originalStart = start != null ? parseTime(start) : 0;

      // reproducir 2 segundos antes
      const startTime = Math.max(originalStart - 2, 0);

      audioEl.currentTime = startTime;

      if (originalStart > 0) audioEl.play();
    };

    audioEl.addEventListener("loadedmetadata", handleLoaded);

    return () => {
      audioEl.removeEventListener("loadedmetadata", handleLoaded);
    };

  }, [open, start]);

  //////////////////////////////////////
  // tiempo audio
  //////////////////////////////////////

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  //////////////////////////////////////
  // salto audio
  //////////////////////////////////////

  const seek = (time) => {

    if (!audioRef.current) return;

    audioRef.current.currentTime = time;
    audioRef.current.play();

  };

  //////////////////////////////////////
  // segmento activo
  //////////////////////////////////////

  const activeIndex = segments.findLastIndex(
    s => currentTime >= s.start
  );
  //////////////////////////////////////
  // autoscroll
  //////////////////////////////////////

  useEffect(()=>{

    if(activeRef.current){

      activeRef.current.scrollIntoView({
        behavior:"smooth",
        block:"center"
      });

    }

  },[activeIndex]);

  //////////////////////////////////////
  // búsqueda
  //////////////////////////////////////

  const matchSearch = (text)=>{
    if(!search) return false;
    return text.toLowerCase().includes(search.toLowerCase());
  };

  //////////////////////////////////////
  // salto automático al primer match
  //////////////////////////////////////

  useEffect(()=>{

    if(!search || segments.length===0) return;

    const found = segments.find(s =>
      s.text.toLowerCase().includes(search.toLowerCase())
    );

    if (found) {
      const startTime = Math.max(found.start - 2, 0);
      seek(startTime);
    }

  },[search]);

  //////////////////////////////////////
  // render
  //////////////////////////////////////

  return (

    <>
      <Button
        icon={<PlayCircleOutlined />}
        type="primary"
        onClick={()=>setOpen(true)}
      >
        Play
      </Button>

      <Modal
        open={open}
        onCancel={() => {
            setOpen(false);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
        }}
        footer={null}
        width={1000}
        title="Archivo de audio"
      >

        <audio
          ref={audioRef}
          src={audio}
          controls
          style={{width:"100%"}}
          onTimeUpdate={handleTimeUpdate}
        />

        <Input
          style={{marginTop:15}}
          placeholder="Buscar en la transcripción..."
          value={search}
          onChange={(e)=>setSearch(e.target.value)}
        />

        <div
          style={{
            marginTop:20,
            maxHeight:400,
            overflowY:"auto",
            background:"#fafafa",
            padding:20,
            borderRadius:8
          }}
        >

          {segments.map((s,i)=>{

            const active = i===activeIndex;
            const match = matchSearch(s.text);

            return(

              <div
                key={i}
                ref={active ? activeRef : null}
                onClick={()=>seek(s.start)}
                style={{
                  padding:8,
                  borderRadius:6,
                  marginBottom:10,
                  cursor:"pointer",
                  background: active
                    ? "#ffe58f"
                    : match
                    ? "#91caff"
                    : "transparent"
                }}
              >

                <Text strong>
                  [{formatTime(s.start)}]
                </Text>

                <span style={{marginLeft:10}}>
                  {s.text}
                </span>

              </div>

            )

          })}

        </div>

      </Modal>
    </>
  );

};

export default Reproductor;